// @/shared/hooks/useServerLogging.ts
import { useEffect, useRef } from "react";
import { AnalyticsUtils } from "@/shared/utils/AnalyticsUtils";
import { type LogCollection } from "@/shared/utils/ServerFetchUtils";

interface UseServerLoggingProps {
    serverHeaders: Record<string, string>;
    serverLogs: LogCollection;
}

export const useServerLogging = ({ serverHeaders, serverLogs }: UseServerLoggingProps) => {
    const headersLoggedRef = useRef(false);
    const logsLoggedRef = useRef(false);

    // Format LogCollection into string literal
    const formatLogsForDataDog = (logCollection: LogCollection): string => {
        const sections: string[] = ["🚀 INITIAL LOGS", ...logCollection.initial, ""];

        if (logCollection.builder?.length) {
            sections.push("🛠️ BUILDER LOGS", ...logCollection.builder, "");
        }

        if (logCollection.statsig?.length) {
            sections.push("📊 STATSIG LOGS", ...logCollection.statsig, "");
        }

        if (logCollection.defaultPlan?.length) {
            sections.push("📋 DEFAULT PLAN LOGS", ...logCollection.defaultPlan, "");
        }

        if (logCollection.breeds?.length) {
            sections.push("🐕 BREEDS LOGS", ...logCollection.breeds, "");
        }

        if (logCollection.giftcard?.length) {
            sections.push("🎁 GIFTCARD LOGS", ...logCollection.giftcard, "");
        }

        if (logCollection.rewards?.length) {
            sections.push("🏆 REWARDS LOGS", ...logCollection.rewards, "");
        }

        if (logCollection.features?.length) {
            sections.push("✨ FEATURES LOGS", ...logCollection.features, "");
        }

        sections.push("🏁 FINAL LOGS", ...logCollection.final);

        return sections.join("\n");
    };

    useEffect(() => {
        // Log headers (only once)
        if (!headersLoggedRef.current) {
            try {
                if (!serverHeaders || Object.keys(serverHeaders).length === 0) {
                    AnalyticsUtils.sendDataDogLog("warn", "NEXT_JS_SERVER - Missing or empty NextJS server headers", {});
                } else {
                    AnalyticsUtils.sendDataDogLog("info", "NEXT_JS_SERVER - Headers", { serverHeaders });
                }
                headersLoggedRef.current = true;
            } catch (err) {
                console.error("Error logging server headers:", err);
            }
        }

        // Log server logs (only once)
        if (!logsLoggedRef.current && serverLogs) {
            try {
                const formattedLogs = formatLogsForDataDog(serverLogs);

                AnalyticsUtils.sendDataDogLog("info", "NEXT_JS_SERVER - Data Fetching Logs", {
                    serverLogs: formattedLogs
                });
                logsLoggedRef.current = true;
            } catch (err) {
                console.error("Error logging server logs:", err);
            }
        }
    }, [serverHeaders, serverLogs]);
};
